import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Check, Lock, ArrowRight } from "lucide-react";

// Detect India-based users so we can show INR pricing. Preferring timezone
// (works even when locale is en-US on an India-provisioned device).
function isIndiaUser() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return true;
    const lang = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (lang.includes("-in") || lang === "hi" || lang.startsWith("hi-")) return true;
  } catch (_) { /* no-op */ }
  return false;
}

// Feature lists reflect the exact quotas enforced by the backend (PLAN_LIMITS).
// Keep in sync with /app/backend/server.py:209.
const PLANS = [
  {
    id: "free",
    name: "Free",
    tagline: "Try Buddy without a card",
    // pricing filled in per-region below
    features: [
      { text: "10 rewrites / day", included: true },
      { text: "5 humanize / day", included: true },
      { text: "3 smart replies / day", included: true },
      { text: "2 free uses of every Pro AI Tool", included: true, hint: "Summarizer · Cover Letter · Resume Check · Plagiarism · Citation · CaptionCraft" },
      { text: "Summarizer input: up to 2,000 words", included: true },
      { text: "All 11 tones & 5 platforms", included: true },
      { text: "Live Grammar & AI Intent Detect", included: true },
      { text: "3 tone variations (Light/Balanced/Strong)", included: false },
      { text: "Confidence scoring", included: false },
      { text: "Translation (20+ languages)", included: false },
      { text: "Voice-to-text", included: false },
      { text: "Saved library", included: false },
      { text: "Chrome extension", included: false },
      { text: "Mobile keyboard", included: false },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Unlimited everything",
    badge: "Most Popular",
    features: [
      { text: "Unlimited rewrites, humanize & smart replies", included: true },
      { text: "Unlimited runs on all 6 Pro AI Tools", included: true, hint: "Summarizer · Cover Letter · Resume Check · Plagiarism · Citation · CaptionCraft" },
      { text: "Summarizer input: up to 6,000 words", included: true },
      { text: "3 tone variations (Light/Balanced/Strong)", included: true },
      { text: "Confidence scoring on every rewrite", included: true },
      { text: "Translation across 20+ languages", included: true },
      { text: "Voice-to-text + AI rewrite", included: true },
      { text: "Saved library with search & tags", included: true },
      { text: "Browser extension (rewrite anywhere you type)", included: true },
      { text: "Mobile keyboard access", included: true },
      { text: "Intent Detect AI (auto-pick best tone)", included: true },
      { text: "Priority AI model & faster responses", included: true },
    ],
  },
  {
    id: "teams",
    name: "Teams",
    tagline: "Brand-consistent for your team",
    features: [
      { text: "Everything in Pro for every seat", included: true },
      { text: "Brand tone presets", included: true },
      { text: "Shared templates & library", included: true },
      { text: "Team admin dashboard", included: true },
      { text: "Per-seat pricing & SSO", included: true },
      { text: "Priority support", included: true },
      { text: "API access", included: true },
      { text: "Custom onboarding", included: true },
    ],
  },
];

// Region-specific price catalog. `perMonth` is shown as the sticker on the
// card; `total` is what Stripe actually charges. `packageIdSuffix` maps to the
// backend PLAN_PACKAGES key (empty for USD → pro_monthly / pro_annual, "_inr"
// → pro_monthly_inr / pro_annual_inr).
const REGION_PRICING = {
  IN: {
    currency: "INR",
    symbol: "₹",
    packageIdSuffix: "_inr",
    pro: {
      monthly: { perMonth: 251, total: 251 },
      annual:  { perMonth: 199, total: 2388 },
    },
    teams: { monthly: { perMonth: 999,  total: 999  }, annual: { perMonth: 799, total: 9588 } },
    freeLabel: "₹0",
  },
  US: {
    currency: "USD",
    symbol: "$",
    packageIdSuffix: "",
    pro: {
      monthly: { perMonth: 12, total: 12 },
      annual:  { perMonth: 8,  total: 96 },
    },
    teams: { monthly: { perMonth: 12, total: 12 }, annual: { perMonth: 9, total: 108 } },
    freeLabel: "$0",
  },
};

export default function PricingPage({ embedded = false }) {
  const [annual, setAnnual] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const region = useMemo(() => (isIndiaUser() ? REGION_PRICING.IN : REGION_PRICING.US), []);
  const { symbol, packageIdSuffix, freeLabel } = region;

  const handleUpgrade = async (planId) => {
    if (!user) { navigate("/login"); return; }
    if (planId === "free" || planId === "teams") return;
    // Backend packages: pro_monthly / pro_annual (USD) and pro_monthly_inr / pro_annual_inr (INR)
    const base = `${planId}_${annual ? "annual" : "monthly"}`;
    const packageId = `${base}${packageIdSuffix}`;
    setLoading(packageId);
    try {
      // India users → Razorpay Standard Checkout. Rest of world → Stripe.
      if (isIndiaUser()) {
        if (!window.Razorpay) { alert("Payment SDK still loading — please try again in a moment."); return; }
        const { data } = await API.post("/razorpay/order", { package_id: packageId });
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: data.name,
          description: data.description,
          order_id: data.order_id,
          prefill: data.prefill,
          theme: { color: "#006039" },
          handler: async (response) => {
            try {
              await API.post("/razorpay/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              window.location.href = `/payment/success?provider=razorpay&order_id=${response.razorpay_order_id}`;
            } catch {
              alert("Payment received but verification failed. Contact support with your payment ID.");
            }
          },
          modal: { ondismiss: () => setLoading(null) },
        });
        rzp.open();
      } else {
        const { data } = await API.post("/stripe/checkout", { package_id: packageId, origin_url: window.location.origin });
        window.location.href = data.url;
      }
    } catch { alert("Failed to start checkout."); }
    finally { setLoading(null); }
  };

  const priceFor = (plan) => {
    if (plan.id === "free") return { display: freeLabel, sub: "" };
    if (plan.id === "teams") return { display: "Contact sales", sub: "Custom pricing for your team" };
    const cycle = annual ? region[plan.id].annual : region[plan.id].monthly;
    const totalLine = annual
      ? `${symbol}${cycle.total.toLocaleString()}/year billed annually`
      : `Billed monthly`;
    return { display: `${symbol}${cycle.perMonth.toLocaleString()}`, sub: totalLine };
  };

  const content = (
    <div className={embedded ? "" : "max-w-5xl mx-auto px-4 pt-8 pb-16"}>
      <div className="text-center mb-10">
        <h2 className="font-display text-3xl font-bold" style={{ color: "var(--text-primary)" }} data-testid="pricing-heading">Simple, Transparent Pricing</h2>
        <p className="mt-2 text-base" style={{ color: "var(--text-muted)" }}>
          Free to start. Pro to unlock all features.
        </p>
        <div className="flex items-center justify-center gap-3 mt-6">
          <span className="text-sm font-medium" style={{ color: annual ? "var(--text-light)" : "var(--text-primary)" }}>Monthly</span>
          <button onClick={() => setAnnual(!annual)} className="relative w-12 h-6 rounded-full transition-colors" style={{ background: annual ? "var(--rolex-green)" : "var(--surface-border-mid)" }} data-testid="pricing-toggle">
            <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm" style={{ left: annual ? "26px" : "2px" }} />
          </button>
          <span className="text-sm font-medium" style={{ color: annual ? "var(--text-primary)" : "var(--text-light)" }}>Annual</span>
          {annual && <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--success-bg)", color: "var(--success)" }}>Save 21%</span>}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto">
        {PLANS.map((plan) => {
          const { display, sub } = priceFor(plan);
          const isCurrentPlan = user?.plan === plan.id;
          const isPro = plan.id === "pro";
          const isTeams = plan.id === "teams";
          return (
            <div key={plan.id} className="buddy-card p-6 flex flex-col relative" style={isPro ? { border: "2px solid var(--rolex-green)", boxShadow: "var(--shadow-md)" } : {}} data-testid={`plan-card-${plan.id}`}>
              {plan.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full" style={{ background: "var(--rolex-green)", color: "white", letterSpacing: "0.05em" }}>{plan.badge.toUpperCase()}</span>}
              <h3 className="font-display text-xl font-semibold" style={{ color: "var(--text-primary)" }}>{plan.name}</h3>
              <p className="text-xs mt-1 mb-4" style={{ color: "var(--text-muted)" }}>{plan.tagline}</p>
              <div className="mb-5">
                {isTeams ? (
                  <>
                    <span className="text-2xl font-bold" style={{ color: "var(--text-primary)" }} data-testid="teams-price-label">Contact sales</span>
                    <p className="text-xs mt-1" style={{ color: "var(--text-light)" }}>Custom pricing for your team</p>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-bold" style={{ color: "var(--text-primary)" }} data-testid={`plan-${plan.id}-price`}>{display}</span>
                    {plan.id !== "free" && <span className="text-sm ml-1" style={{ color: "var(--text-muted)" }}>/mo</span>}
                    {sub && plan.id !== "free" && <p className="text-xs mt-1" style={{ color: "var(--text-light)" }} data-testid={`plan-${plan.id}-billing`}>{sub}</p>}
                  </>
                )}
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.included ? <Check size={15} className="shrink-0 mt-0.5" style={{ color: "var(--rolex-green)" }} /> : <Lock size={13} className="shrink-0 mt-0.5" style={{ color: "var(--rolex-gold)", opacity: 0.6 }} />}
                    <span style={{ color: f.included ? "var(--text-secondary)" : "var(--text-light)" }}>
                      {f.text}
                      {f.hint && <span className="block text-xs mt-0.5" style={{ color: "var(--text-light)" }}>{f.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {isCurrentPlan ? <div className="btn-secondary text-center py-2.5 text-sm opacity-60 cursor-default">Current Plan</div>
              : plan.id === "free" ? <button onClick={() => navigate(user ? "/app" : "/login")} className="btn-secondary py-2.5 text-sm w-full" data-testid="plan-free-btn">{user ? "Go to Dashboard" : "Start Free"}</button>
              : plan.id === "teams" ? <button onClick={() => window.location.href = "mailto:teams@buddy.app?subject=Buddy%20Teams%20Plan%20Inquiry"} className="btn-gold py-2.5 text-sm w-full" data-testid="plan-teams-btn">Contact Sales</button>
              : <button onClick={() => handleUpgrade(plan.id)} className="btn-primary py-2.5 text-sm w-full flex items-center justify-center gap-1.5" disabled={!!loading} data-testid={`plan-${plan.id}-btn`}>
                  {loading?.startsWith(`${plan.id}_${annual ? "annual" : "monthly"}`)
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <>Upgrade to {plan.name} <ArrowRight size={14} /></>}
                </button>}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (embedded) return content;
  return <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}><Navbar />{content}</div>;
}
